import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';

export const OPERADORAS = ['Claro', 'Vivo', 'TIM', 'Oi', 'Algar', 'Outra'];
export const TIPOS_CONTRATACAO = ['Linha Nova', 'Portabilidade', 'Migração'];
const TIPOS_MIGRACAO = ['Pré-pago', 'Controle', 'Pós', 'TT'];

// Plano "Pós-Pago ou superior" libera linhas dependentes (flag do admin em Valores ou detecção pelo nome)
export const planoPermiteDependentes = (plano) => {
  if (!plano) return false;
  if (typeof plano.permite_dependentes === 'boolean') return plano.permite_dependentes;
  return /P[OÓ]S|BLACK|FAM[IÍ]LIA/i.test(plano.nome_plano || plano.nome || '');
};

const digitos = (v) => (v || '').replace(/\D/g, '');

// Validação compartilhada (Nova Venda e correção). Retorna mensagem de erro ou null.
export const validarMovel = (d) => {
  if (['Portabilidade', 'Migração'].includes(d.movel_tipo_operacao)) {
    const nums = d.movel_numeros || [];
    if (nums.length === 0) return `Informe ao menos um número para ${d.movel_tipo_operacao.toLowerCase()}`;
    for (const [i, n] of nums.entries()) {
      if (digitos(n.numero).length < 10) return `Número ${i + 1} da ${d.movel_tipo_operacao.toLowerCase()}: informe DDD + número`;
      if (!n.operadora) return `Número ${i + 1}: selecione a operadora`;
    }
  }
  if (d.movel_possui_dependentes === true) {
    const deps = d.movel_dependentes || [];
    if (deps.length === 0) return 'Adicione ao menos uma linha dependente ou marque "Não"';
    for (const [i, dep] of deps.entries()) {
      if (!dep.tipo) return `Dependente ${i + 1}: selecione o tipo (Linha nova, Portabilidade ou Migração)`;
      if (dep.tipo !== 'Linha Nova') {
        if (digitos(dep.numero).length < 10) return `Dependente ${i + 1}: informe DDD + número`;
        if (!dep.operadora) return `Dependente ${i + 1}: selecione a operadora`;
      }
    }
  }
  return null;
};

const CORES = {
  red: 'bg-red-600 text-white border-red-600',
  orange: 'bg-orange-600 text-white border-orange-600',
  purple: 'bg-purple-600 text-white border-purple-600',
};

const Chip = ({ ativo, onClick, children, testid, cor = 'red' }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
      ativo ? CORES[cor] : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-red-400'
    }`}
  >
    {children}
  </button>
);

const OperadoraSelect = ({ value, onChange, testid }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="h-9 rounded-md border border-input bg-background px-2 text-xs w-full"
  >
    <option value="">Operadora...</option>
    {OPERADORAS.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const ListaNumeros = ({ titulo, numeros, onChange, prefixo }) => {
  const lista = numeros && numeros.length ? numeros : [{ numero: '', operadora: '' }];
  const set = (idx, patch) => onChange(lista.map((n, i) => (i === idx ? { ...n, ...patch } : n)));
  return (
    <div className="space-y-2 p-3 rounded-lg border border-dashed border-red-300/60 bg-red-50/40 dark:bg-red-950/10" data-testid={`${prefixo}-lista`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold text-red-700 dark:text-red-300">{titulo} ({lista.filter((n) => digitos(n.numero).length >= 10).length})</Label>
        <button type="button" onClick={() => onChange([...lista, { numero: '', operadora: '' }])} className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-[11px] rounded" data-testid={`${prefixo}-add`}>
          <Plus className="h-3 w-3" /> Adicionar outro número
        </button>
      </div>
      {lista.map((n, idx) => (
        <div key={idx} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`${prefixo}-item-${idx}`}>
          <Input value={n.numero} onChange={(e) => set(idx, { numero: e.target.value })} placeholder={`Número ${idx + 1}: (00) 00000-0000`} className="h-9 text-xs" data-testid={`${prefixo}-numero-${idx}`} />
          <OperadoraSelect value={n.operadora} onChange={(v) => set(idx, { operadora: v })} testid={`${prefixo}-operadora-${idx}`} />
          <button type="button" disabled={lista.length === 1} onClick={() => onChange(lista.filter((_, i) => i !== idx))} className="text-red-500 disabled:opacity-30 px-1" data-testid={`${prefixo}-remove-${idx}`} title="Remover"><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
};

export const MovelContratacao = ({ data, onChange, plano, tipoVenda }) => {
  const permiteDep = planoPermiteDependentes(plano);
  const tipo = data.movel_tipo_operacao || '';
  const numeros = data.movel_numeros || [];
  const deps = data.movel_dependentes || [];

  // Mantém campos legados (1º número) sincronizados para exportações/relatórios antigos
  const setNumeros = (lista) => {
    const primeiro = lista[0] || {};
    onChange({
      movel_numeros: lista,
      movel_numero_portabilidade: tipo === 'Portabilidade' ? primeiro.numero || '' : '',
      movel_operadora_portabilidade: tipo === 'Portabilidade' ? primeiro.operadora || '' : '',
      movel_numero_migracao: tipo === 'Migração' ? primeiro.numero || '' : '',
    });
  };

  const escolherTipo = (op) => {
    const novo = tipo === op ? '' : op;
    onChange({
      movel_tipo_operacao: novo,
      movel_numeros: novo === 'Linha Nova' || !novo ? [] : numeros.length ? numeros : [{ numero: '', operadora: '' }],
      movel_numero_portabilidade: '', movel_operadora_portabilidade: '', movel_numero_migracao: '',
      movel_tipo_migracao: novo === 'Migração' ? data.movel_tipo_migracao || '' : '',
    });
  };

  const setDep = (idx, patch) => onChange({ movel_dependentes: deps.map((d, i) => (i === idx ? { ...d, ...patch } : d)) });

  return (
    <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3" data-testid="movel-opcoes">
      <Label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Tipo de contratação</Label>
      <div className="flex gap-2 flex-wrap">
        {TIPOS_CONTRATACAO.map((op) => (
          <Chip key={op} ativo={tipo === op} onClick={() => escolherTipo(op)} testid={`movel-tipo-${op.toLowerCase().replace(/[^a-z]/g, '')}`}>{op}</Chip>
        ))}
      </div>

      {tipo === 'Portabilidade' && <ListaNumeros titulo="Números para portabilidade" numeros={numeros} onChange={setNumeros} prefixo="movel-port" />}

      {tipo === 'Migração' && (
        <div className="space-y-2">
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-[11px] text-gray-500">Tipo de migração (opcional):</span>
            {TIPOS_MIGRACAO.map((t) => (
              <Chip key={t} cor="orange" ativo={data.movel_tipo_migracao === t} onClick={() => onChange({ movel_tipo_migracao: data.movel_tipo_migracao === t ? '' : t })} testid={`movel-migracao-${t.toLowerCase().replace(/[^a-z]/g, '')}`}>{t}</Chip>
            ))}
          </div>
          <ListaNumeros titulo="Números para migração" numeros={numeros} onChange={setNumeros} prefixo="movel-mig" />
        </div>
      )}

      {permiteDep && (
        <div className="space-y-2 p-3 rounded-lg border border-purple-300/60 bg-purple-50/40 dark:bg-purple-950/10" data-testid="movel-dependentes">
          <Label className="text-xs font-bold text-purple-700 dark:text-purple-300">Dependentes — esta contratação possui linha(s) dependente(s)?</Label>
          <div className="flex gap-2">
            <Chip cor="purple" ativo={data.movel_possui_dependentes === true} onClick={() => onChange({ movel_possui_dependentes: true, movel_dependentes: deps.length ? deps : [{ tipo: '', numero: '', operadora: '' }] })} testid="movel-dep-sim">Sim</Chip>
            <Chip cor="purple" ativo={data.movel_possui_dependentes === false} onClick={() => onChange({ movel_possui_dependentes: false, movel_dependentes: [] })} testid="movel-dep-nao">Não</Chip>
          </div>
          {data.movel_possui_dependentes === true && (
            <div className="space-y-2">
              {deps.map((dep, idx) => (
                <div key={idx} className="p-2 rounded border border-purple-200 dark:border-purple-800 space-y-2" data-testid={`movel-dep-item-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">Linha dependente {idx + 1}</span>
                    <button type="button" onClick={() => onChange({ movel_dependentes: deps.filter((_, i) => i !== idx) })} className="text-red-500 px-1" data-testid={`movel-dep-remove-${idx}`}><X className="h-4 w-4" /></button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {TIPOS_CONTRATACAO.map((op) => (
                      <Chip key={op} cor="purple" ativo={dep.tipo === op} onClick={() => setDep(idx, { tipo: op, ...(op === 'Linha Nova' ? { numero: '', operadora: '' } : {}) })} testid={`movel-dep-${idx}-tipo-${op.toLowerCase().replace(/[^a-z]/g, '')}`}>{op}</Chip>
                    ))}
                  </div>
                  {dep.tipo && dep.tipo !== 'Linha Nova' && (
                    <div className="grid grid-cols-[1fr_140px] gap-2">
                      <Input value={dep.numero || ''} onChange={(e) => setDep(idx, { numero: e.target.value })} placeholder="(00) 00000-0000" className="h-9 text-xs" data-testid={`movel-dep-numero-${idx}`} />
                      <OperadoraSelect value={dep.operadora} onChange={(v) => setDep(idx, { operadora: v })} testid={`movel-dep-operadora-${idx}`} />
                    </div>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => onChange({ movel_dependentes: [...deps, { tipo: '', numero: '', operadora: '' }] })} className="flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-[11px] rounded" data-testid="movel-dep-add">
                <Plus className="h-3 w-3" /> Adicionar dependente
              </button>
            </div>
          )}
        </div>
      )}

      {tipoVenda === 'TT' && (
        <div className="space-y-1">
          <Label className="text-xs font-semibold text-gray-600 dark:text-gray-300">CPF do Cliente (Linha Móvel)</Label>
          <Input placeholder="000.000.000-00" value={data.movel_cpf_cliente || ''} onChange={(e) => onChange({ movel_cpf_cliente: e.target.value })} data-testid="movel-cpf-cliente" />
        </div>
      )}
    </div>
  );
};

// Exibição somente leitura (Detalhes da Venda)
export const MovelResumo = ({ venda }) => {
  const numeros = venda.movel_numeros && venda.movel_numeros.length
    ? venda.movel_numeros
    : (venda.movel_numero_portabilidade || venda.movel_numero_migracao)
      ? [{ numero: venda.movel_numero_portabilidade || venda.movel_numero_migracao, operadora: venda.movel_operadora_portabilidade || '' }]
      : [];
  const deps = venda.movel_dependentes || [];
  if (!venda.movel_tipo_operacao && numeros.length === 0 && deps.length === 0) return null;
  return (
    <div className="mt-3 p-3 rounded-lg border border-orange-200 dark:border-orange-900 bg-orange-50/40 dark:bg-orange-950/10 space-y-2" data-testid="movel-resumo">
      <p className="text-xs uppercase tracking-wide text-gray-500">Móvel — {venda.movel_tipo_operacao || 'Linha Nova'}{venda.movel_tipo_migracao ? ` (${venda.movel_tipo_migracao})` : ''}</p>
      {numeros.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {numeros.map((n, i) => (
            <span key={i} className="px-2 py-1 rounded bg-white dark:bg-gray-900 border text-xs font-mono" data-testid={`movel-resumo-numero-${i}`}>{n.numero} <span className="text-gray-500">| {n.operadora || '-'}</span></span>
          ))}
        </div>
      )}
      {deps.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-purple-700 dark:text-purple-300">Linhas dependentes ({deps.length})</p>
          <ul className="text-xs mt-1 space-y-0.5">
            {deps.map((d, i) => (
              <li key={i} data-testid={`movel-resumo-dep-${i}`}>• {d.tipo || '-'}{d.numero ? ` — ${d.numero} | ${d.operadora || '-'}` : ''}</li>
            ))}
          </ul>
        </div>
      )}
      {venda.movel_possui_dependentes === false && <p className="text-xs text-gray-500">Sem linhas dependentes</p>}
    </div>
  );
};

export default MovelContratacao;
